'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { loadProductionState, saveProductionState, type ProductionShift } from '@/features/kitchen/storage/productionStorage'
import {
  fetchTodayProductionPlan,
  fetchTodayProductionSessions,
  shouldUseProductionApi,
  type ApiProductionSession,
  type ApiTodayPlan,
} from '@/features/kitchen/api/getTodayProduction'
import { Link } from '@/shared/navigation'
import { postProductionSession } from '@/features/kitchen/api/postProduction'
import { downloadTodayProductionPlanPdf } from '@/features/kitchen/api/downloadTodayProductionPlanPdf'
import { fetchKitchenStock } from '@/features/kitchen/api/getDispatchOptions'
import { fetchTodayDeliveries, type ApiDelivery } from '@/features/kitchen/api/getTodayDispatch'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { kitchenTodayISO } from '@/features/kitchen/lib/kitchenDates'
import { buildPlanCodeFallback, buildProductionRefPrefix } from '@/features/kitchen/productionCodes'
import { PlanProductionTable } from '@/features/kitchen/PlanProductionTable'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import {
  computePlanLineRows,
  groupSessionLinesByCategory,
  buildPlanCatalogFromTodayPlan,
  formatNeedCell,
  needCellClass,
  productionNeed,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'
import type { ProductionLineRecord } from '@/features/kitchen/storage/productionStorage'

type SessionStatus = 'in_progress' | 'draft' | 'posted'

type ProductionLine = ProductionLineRecord

type ProductionSession = {
  ref: string
  title: string
  updatedAt: string
  status: SessionStatus
  shift?: ProductionShift
  note?: string
}

type TodayPlanDetail = {
  planCode: string
  notes?: string
  catalog: PlanCatalogItem[]
}

const DEFAULT_PLAN_CATALOG: PlanCatalogItem[] = [
  { sku: 'Roti coklat', plan: 100, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Roti keju', plan: 80, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat gula', plan: 50, categoryName: 'Roti Manis', categorySortOrder: 1 },
]

const PRODUCTION_FLOW_STEPS = kitchenTerms.production.flowSteps.map((label) => ({ label }))

const DEFAULT_SESSION_SHIFT: ProductionShift = 'MORNING'

function buildMergedCatalog(plan: ApiTodayPlan): PlanCatalogItem[] {
  return buildPlanCatalogFromTodayPlan(plan)
}

function onlyPlanLines(lines: ProductionLine[]): ProductionLine[] {
  return lines.filter((line) => line.source === 'plan')
}

function formatSessionDateLabel(dateIso: string): string {
  const date = new Date(`${dateIso}T12:00:00`)
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date)
}

function buildSessionTitle(dateIso: string): string {
  return formatSessionDateLabel(dateIso)
}

function productionFlowActiveStep(
  status: SessionStatus,
  editorStep: 'edit' | 'review',
): number {
  if (status === 'posted') return PRODUCTION_FLOW_STEPS.length + 1
  if (editorStep === 'review') return 2
  return 1
}

const DEFAULT_GOOD_STOCK: Record<string, number> = {
  'Roti coklat': 100,
  'Roti keju': 60,
  'Donat gula': 0,
}

function dispatchedQtyFromDeliveries(deliveries: ApiDelivery[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const delivery of deliveries) {
    if (delivery.status === 'prepared' || delivery.status === 'draft') continue
    for (const line of delivery.lines) {
      if (line.qty <= 0 || !line.product_name.trim()) continue
      const name = line.product_name.trim()
      out[name] = (out[name] ?? 0) + line.qty
    }
  }
  return out
}

function qtyForProduct(map: Record<string, number>, productName: string): number {
  const exact = map[productName]
  if (exact !== undefined) return exact
  const lower = productName.trim().toLowerCase()
  for (const [name, qty] of Object.entries(map)) {
    if (name.toLowerCase() === lower) return qty
  }
  return 0
}

function getActualInputHint(
  actual: number,
  need: number,
): { text: string; className: string } {
  const t = kitchenTerms.production.hints
  if (actual === 0) {
    if (need <= 0) {
      return { text: t.stockEnough, className: 'text-on-surface-variant' }
    }
    return { text: t.needProduction(need), className: 'text-on-surface-variant' }
  }
  if (need <= 0) {
    return { text: t.outsideNeed, className: 'text-status-success' }
  }
  const diff = actual - need
  if (diff === 0) return { text: t.needMet, className: 'text-status-success' }
  if (diff > 0) {
    return { text: t.overNeed(diff), className: 'text-status-success' }
  }
  return { text: t.underNeed(Math.abs(diff)), className: 'text-status-warning' }
}

function stockQtyForProduct(stockByProduct: Record<string, number>, productName: string): number {
  return qtyForProduct(stockByProduct, productName)
}

function nextSessionRef(date: string, sessions: ProductionSession[], kitchenCode: string): string {
  const prefix = buildProductionRefPrefix(date, kitchenCode)
  let maxSeq = 0

  for (const session of sessions) {
    if (!session.ref.startsWith(prefix)) continue
    const seq = Number.parseInt(session.ref.slice(prefix.length), 10)
    if (!Number.isNaN(seq)) maxSeq = Math.max(maxSeq, seq)
  }

  return `${prefix}${String(maxSeq + 1).padStart(2, '0')}`
}

function isPosted(status: SessionStatus): boolean {
  return status === 'posted'
}

function createLinesFromPlan(planCatalog: PlanCatalogItem[]): ProductionLine[] {
  return planCatalog.map((item, index) => ({
    id: `p-${Date.now()}-${index}`,
    productName: item.sku,
    source: 'plan' as const,
    planQty: item.plan,
    qty: 0,
  }))
}

function formatUpdatedAt(): string {
  return `Diperbarui ${new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date())}`
}

function formatPostedAt(iso?: string): string {
  const date = iso ? new Date(iso) : new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `Sudah masuk stok ${day} ${time}`
}

function getFilledLines(lines: ProductionLine[]): ProductionLine[] {
  return lines.filter((line) => line.qty > 0 && line.productName.trim())
}

function formatSessionUpdatedAt(iso: string, status: SessionStatus): string {
  if (status === 'posted') return formatPostedAt(iso)
  try {
    return `Diperbarui ${new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))}`
  } catch {
    return 'Diperbarui'
  }
}

function mapApiSession(session: ApiProductionSession, planDate: string): ProductionSession {
  const shift = (['MORNING', 'NOON', 'EVENING'].includes(session.session_code)
    ? session.session_code
    : 'MORNING') as ProductionShift
  return {
    ref: session.ref,
    title: buildSessionTitle(planDate),
    shift,
    updatedAt: formatSessionUpdatedAt(session.updated_at, session.status),
    status: session.status,
    note: session.note,
  }
}

function mapApiSessionLines(lines: ApiProductionSession['lines']): ProductionLine[] {
  return lines.map((line, index) => ({
    id: `api-${index}-${line.product_name}`,
    productName: line.product_name,
    source: line.source,
    planQty: line.plan_qty,
    qty: line.qty,
  }))
}

export function KitchenProductionPage() {
  const useApi = shouldUseProductionApi()
  const initialProduction = useApi
    ? { sessions: [] as ProductionSession[], sessionLinesByRef: {} as Record<string, ProductionLine[]> }
    : loadProductionState()
  const [view, setView] = useState<'plan' | 'sessions'>('plan')
  const [sessionFilter, setSessionFilter] = useState<'todo' | 'history'>('todo')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [sessionDate, setSessionDate] = useState(() => kitchenTodayISO())
  const [createError, setCreateError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [selectedSessionRef, setSelectedSessionRef] = useState<string | null>(null)
  const [editorStep, setEditorStep] = useState<'edit' | 'review'>('edit')
  const [isPinModalOpen, setIsPinModalOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [sessionLinesByRef, setSessionLinesByRef] = useState<Record<string, ProductionLine[]>>(
    initialProduction.sessionLinesByRef,
  )
  const [editorNote, setEditorNote] = useState('')

  const device = getKitchenDevice()
  const kitchenName = device?.kitchenName ?? 'Dapur'
  const kitchenCode = device?.kitchenCode ?? 'DAPUR-A'

  const todayFull = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const [planDetails, setPlanDetails] = useState<TodayPlanDetail[]>([])
  const [showPlanDetails, setShowPlanDetails] = useState(false)
  const [apiLoadError, setApiLoadError] = useState<string | null>(null)
  const [isLoadingPlan, setIsLoadingPlan] = useState(useApi)
  const [refreshKey, setRefreshKey] = useState(0)
  const [sessions, setSessions] = useState<ProductionSession[]>(initialProduction.sessions)
  const [stockByProduct, setStockByProduct] = useState<Record<string, number>>(DEFAULT_GOOD_STOCK)
  const [dispatchedByProduct, setDispatchedByProduct] = useState<Record<string, number>>({})

  const [mergedCatalog, setMergedCatalog] = useState<PlanCatalogItem[]>(DEFAULT_PLAN_CATALOG)
  const [planLabel, setPlanLabel] = useState(() =>
    buildPlanCodeFallback(kitchenTodayISO(), kitchenCode),
  )
  const planCatalog = mergedCatalog
  const planCode = planLabel
  const planStatus = mergedCatalog.length > 0 ? 'published' : 'none'

  const applyProductionPayload = useCallback(
    (
      plan: Awaited<ReturnType<typeof fetchTodayProductionPlan>>,
      sessionPayload: Awaited<ReturnType<typeof fetchTodayProductionSessions>>,
      stockItems: Awaited<ReturnType<typeof fetchKitchenStock>>,
      deliveryPayload: Awaited<ReturnType<typeof fetchTodayDeliveries>>,
    ) => {
      const stockMap: Record<string, number> = {}
      for (const item of stockItems) {
        stockMap[item.productName] = item.stockAvailable
      }
      setStockByProduct(stockMap)
      setDispatchedByProduct(dispatchedQtyFromDeliveries(deliveryPayload.deliveries ?? []))

      const merged = buildMergedCatalog(plan)
      setMergedCatalog(merged)
      setPlanDetails(
        (plan.plans ?? []).map((item) => ({
          planCode: item.plan_code,
          notes: item.notes,
          catalog: item.lines.map((line) => ({
            sku: line.product_name,
            plan: line.target_qty,
            categoryName: line.category_name?.trim() || 'Lainnya',
            categorySortOrder: line.category_sort_order ?? 9999,
          })),
        })),
      )
      setPlanLabel(
        plan.plans.length === 1
          ? plan.plans[0].plan_code
          : plan.plans.length > 1
            ? `${plan.plans.length} plan HO · ${kitchenName}`
            : buildPlanCodeFallback(plan.plan_date || kitchenTodayISO(), kitchenCode),
      )

      const planDate = plan.plan_date || kitchenTodayISO()
      const catalogForLines = merged.length > 0 ? merged : DEFAULT_PLAN_CATALOG
      const nextSessions = sessionPayload.sessions.map((session) => mapApiSession(session, planDate))
      const nextLines: Record<string, ProductionLine[]> = {}
      for (const session of sessionPayload.sessions) {
        if (session.lines.length > 0) {
          nextLines[session.ref] = mapApiSessionLines(session.lines)
        } else if (session.status !== 'posted' && catalogForLines.length > 0) {
          nextLines[session.ref] = createLinesFromPlan(catalogForLines)
        } else {
          nextLines[session.ref] = []
        }
      }
      setSessions(nextSessions)
      setSessionLinesByRef(nextLines)
    },
    [kitchenCode, kitchenName],
  )

  const reloadProductionData = useCallback(
    async (options?: { showLoading?: boolean }) => {
      if (!useApi || !device?.deviceId) return

      if (options?.showLoading !== false) {
        setIsLoadingPlan(true)
      }
      setApiLoadError(null)

      try {
        const [plan, sessionPayload, stockItems, deliveryPayload] = await Promise.all([
          fetchTodayProductionPlan(device.deviceId),
          fetchTodayProductionSessions(device.deviceId),
          fetchKitchenStock(device.deviceId),
          fetchTodayDeliveries(device.deviceId),
        ])
        applyProductionPayload(plan, sessionPayload, stockItems, deliveryPayload)
      } catch {
        setApiLoadError(kitchenTerms.production.loadError)
      } finally {
        if (options?.showLoading !== false) {
          setIsLoadingPlan(false)
        }
      }
    },
    [useApi, device?.deviceId, applyProductionPayload],
  )

  useEffect(() => {
    if (!useApi) {
      saveProductionState({ sessions, sessionLinesByRef })
    }
  }, [sessions, sessionLinesByRef, useApi])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return undefined
    void reloadProductionData()
    return undefined
  }, [useApi, device?.deviceId, reloadProductionData, refreshKey])

  useEffect(() => {
    if (!useApi) return undefined
    const bump = () => setRefreshKey((k) => k + 1)
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump()
    }
    window.addEventListener('focus', bump)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', bump)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [useApi])

  const planLines = useMemo(
    () =>
      computePlanLineRows(
        sessions,
        sessionLinesByRef,
        planCatalog,
        stockByProduct,
        dispatchedByProduct,
        qtyForProduct,
        stockQtyForProduct,
      ),
    [sessions, sessionLinesByRef, planCatalog, stockByProduct, dispatchedByProduct],
  )

  const planPct = useMemo(() => {
    const totalNeed = planLines.reduce((sum, line) => sum + Math.max(0, line.need), 0)
    const totalActual = planLines.reduce((sum, line) => sum + line.totalActual, 0)
    if (totalNeed <= 0) return totalActual > 0 ? 100 : 0
    return Math.min(100, Math.round((totalActual / totalNeed) * 100))
  }, [planLines])

  const sessionLineGroups = useMemo(() => {
    if (!selectedSessionRef) return []
    const lines = onlyPlanLines(sessionLinesByRef[selectedSessionRef] ?? [])
    return groupSessionLinesByCategory(lines, planCatalog)
  }, [selectedSessionRef, sessionLinesByRef, planCatalog])

  const [isDownloadingPlan, setIsDownloadingPlan] = useState(false)
  const [deleteTargetRef, setDeleteTargetRef] = useState<string | null>(null)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const todoSessions = useMemo(() => sessions.filter((s) => !isPosted(s.status)), [sessions])
  const historyCount = sessions.filter((s) => isPosted(s.status)).length
  const hasActiveTodo = todoSessions.length > 0

  async function handleDownloadPlanPdf() {
    if (!useApi) {
      showToast(kitchenTerms.production.toast.pdfNeedsApi)
      return
    }
    setIsDownloadingPlan(true)
    try {
      await downloadTodayProductionPlanPdf()
      showToast(kitchenTerms.production.toast.pdfSuccess)
    } catch (err) {
      showToast(err instanceof Error ? err.message : kitchenTerms.production.toast.pdfFailed)
    } finally {
      setIsDownloadingPlan(false)
    }
  }

  function getSessionSummary(session: ProductionSession): string {
    const lines = sessionLinesByRef[session.ref] ?? []
    const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
    const skuCount = lines.length
    if (skuCount === 0) return kitchenTerms.production.sessionSummary.empty
    return kitchenTerms.production.sessionSummary.filled(skuCount, totalQty)
  }

  function openCreateModal() {
    setSessionDate(kitchenTodayISO())
    setCreateError(null)
    setIsCreateOpen(true)
  }

  function createNewSession(forDate?: string) {
    const date = forDate ?? sessionDate
    if (!date) {
      setCreateError(kitchenTerms.production.errors.dateRequired)
      return
    }
    if (mergedCatalog.length === 0) {
      setCreateError(kitchenTerms.production.errors.noPlan)
      return
    }
    const ref = nextSessionRef(date, sessions, kitchenCode)

    const next: ProductionSession = {
      ref,
      title: buildSessionTitle(date),
      shift: DEFAULT_SESSION_SHIFT,
      updatedAt: kitchenTerms.production.created,
      status: 'draft',
    }

    setSessions((prev) => [next, ...prev])
    setSessionLinesByRef((prev) => ({
      ...prev,
      [ref]: createLinesFromPlan(mergedCatalog),
    }))
    setSelectedSessionRef(ref)
    setEditorNote('')
    setEditorStep('edit')
    setSaveError(null)
    setSessionFilter('todo')
    setView('sessions')
    setIsCreateOpen(false)
    showToast(kitchenTerms.production.toast.sessionCreated)
  }

  function startProductionToday() {
    const todayIso = kitchenTodayISO()
    if (mergedCatalog.length === 0) {
      showToast(kitchenTerms.production.toast.noPlan)
      return
    }
    setSessionDate(todayIso)
    createNewSession(todayIso)
  }

  function canDeleteSession(session: ProductionSession): boolean {
    if (isPosted(session.status)) return false
    const lines = sessionLinesByRef[session.ref] ?? []
    return getFilledLines(lines).length === 0
  }

  function deleteDraftSession(ref: string) {
    const session = sessions.find((s) => s.ref === ref)
    if (!session || !canDeleteSession(session)) return
    setSessions((prev) => prev.filter((s) => s.ref !== ref))
    setSessionLinesByRef((prev) => {
      const next = { ...prev }
      delete next[ref]
      return next
    })
    showToast(kitchenTerms.production.toast.deleted)
    setDeleteTargetRef(null)
  }

  function updateLine(selectedRef: string, lineId: string, patch: Partial<ProductionLine>) {
    const current = sessionLinesByRef[selectedRef] ?? []
    const next = current.map((line) => (line.id === lineId ? { ...line, ...patch } : line))
    setSessionLinesByRef((prev) => ({ ...prev, [selectedRef]: next }))
  }

  function selectSession(ref: string) {
    const session = sessions.find((s) => s.ref === ref)
    setSelectedSessionRef(ref)
    setEditorNote(session?.note ?? '')
    setEditorStep('edit')
    setSaveError(null)
  }

  function closeSession() {
    setSelectedSessionRef(null)
    setEditorStep('edit')
    setIsPinModalOpen(false)
    setSaveError(null)
  }

  function validateFilledLines(lines: ProductionLine[]): boolean {
    const hasQty = getFilledLines(lines).length > 0
    if (!hasQty) {
      setSaveError(kitchenTerms.production.toast.saveMinOne)
      return false
    }
    setSaveError(null)
    return true
  }

  function persistSessionDraft(ref: string, note: string) {
    setSessions((prev) =>
      prev.map((session) =>
        session.ref === ref
          ? { ...session, status: session.status === 'posted' ? 'posted' : 'in_progress', note, updatedAt: formatUpdatedAt() }
          : session,
      ),
    )
  }

  function saveProductionResult() {
    if (!selectedSessionRef) return
    const lines = sessionLinesByRef[selectedSessionRef] ?? []
    if (!validateFilledLines(lines)) return

    persistSessionDraft(selectedSessionRef, editorNote)
    closeSession()
    setSessionFilter('todo')
    showToast(kitchenTerms.production.toast.saved)
  }

  function openStockReview() {
    if (!selectedSessionRef) return
    const lines = sessionLinesByRef[selectedSessionRef] ?? []
    if (!validateFilledLines(lines)) return

    persistSessionDraft(selectedSessionRef, editorNote)
    setEditorStep('review')
  }

  async function confirmPostToStock(pin: string) {
    if (!selectedSessionRef || !device) {
      throw new Error(kitchenTerms.production.errors.deviceRequired)
    }

    const lines = getFilledLines(onlyPlanLines(sessionLinesByRef[selectedSessionRef] ?? []))
    const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
    const result = await postProductionSession({
      sessionRef: selectedSessionRef,
      deviceId: device.deviceId,
      pin,
      lines: lines.map((line) => ({
        productName: line.productName.trim(),
        qty: line.qty,
        source: line.source,
      })),
      note: editorNote.trim() || undefined,
    })

    const postedLabel = formatPostedAt(result.posted_at)
    setSessions((prev) =>
      prev.map((session) =>
        session.ref === selectedSessionRef
          ? { ...session, status: 'posted', note: editorNote, updatedAt: postedLabel }
          : session,
      ),
    )
    setIsPinModalOpen(false)
    closeSession()
    setSessionFilter('history')
    await reloadProductionData({ showLoading: false })
    showToast(kitchenTerms.production.toast.posted(totalQty, consumeLastSyncQueued()))
  }

  const orderedSessions = [...sessions].sort((a, b) => {
    const rank = { in_progress: 0, draft: 1, posted: 2 } as const
    return rank[a.status] - rank[b.status]
  })

  const visibleSessions = orderedSessions.filter((session) =>
    sessionFilter === 'todo' ? !isPosted(session.status) : isPosted(session.status),
  )

  const selectedSession = sessions.find((session) => session.ref === selectedSessionRef) ?? null
  const selectedLines = selectedSession
    ? onlyPlanLines(sessionLinesByRef[selectedSession.ref] ?? [])
    : []
  const filledLines = getFilledLines(selectedLines)
  const filledTotalQty = filledLines.reduce((sum, line) => sum + line.qty, 0)
  const planStatusLabel =
    planStatus === 'published'
      ? kitchenTerms.production.planStatusPublished
      : kitchenTerms.production.planStatusNone

  return (
    <section className="space-y-4">
      <div className="card relative">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoadingPlan}
          aria-label={kitchenTerms.production.refreshAria}
          title={kitchenTerms.production.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoadingPlan ? 'animate-spin' : ''}`}>
            refresh
          </span>
        </button>
        <div className="pr-12">
          <p className="text-headline-md text-primary break-words">{kitchenTerms.production.planTitle}</p>
          <p className="text-body-sm text-on-surface-variant capitalize mt-1">
            {kitchenName} · {todayFull}
          </p>
        </div>
      </div>

      <div className="card p-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              setView('plan')
              closeSession()
              void reloadProductionData({ showLoading: false })
            }}
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
              view === 'plan' ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-variant bg-surface'
            }`}
          >
            {kitchenTerms.production.plan}
          </button>
          <button
            type="button"
            onClick={() => {
              setView('sessions')
              setSessionFilter('todo')
            }}
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
              view === 'sessions'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
          >
            {kitchenTerms.production.session}
          </button>
        </div>
      </div>

      {view === 'plan' && (
        <div className="card space-y-3">
          <div>
            <p className="font-mono text-body-sm text-on-surface-variant">{planCode}</p>
            <p className="text-body-sm text-on-surface-variant mt-2 rounded-lg bg-surface-container border border-outline-variant/60 px-3 py-2">
              {kitchenTerms.production.brdHint}
            </p>
            <p className="text-body-sm text-on-surface-variant mt-2">
              {kitchenTerms.production.planTargetLabel(planStatusLabel)}
            </p>
            {planDetails.length > 1 && (
              <button
                type="button"
                onClick={() => setShowPlanDetails((prev) => !prev)}
                className="text-body-sm text-primary font-semibold mt-2 underline-offset-2 hover:underline"
              >
                {showPlanDetails
                  ? kitchenTerms.production.hidePlanDetails
                  : kitchenTerms.production.showPlanDetails(planDetails.length)}
              </button>
            )}
          </div>

          {showPlanDetails && planDetails.length > 0 && (
            <div className="space-y-2 border border-outline-variant rounded-lg p-3 bg-surface-container">
              {planDetails.map((detail) => (
                <div key={detail.planCode} className="text-body-sm">
                  <p className="font-mono font-semibold text-on-surface">{detail.planCode}</p>
                  {detail.notes && (
                    <p className="text-on-surface-variant mt-0.5">{detail.notes}</p>
                  )}
                  <p className="text-label-md text-on-surface-variant mt-1">
                    {detail.catalog.map((line) => `${line.sku} ${line.plan}`).join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {apiLoadError && (
            <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
              <span className="text-body-sm">{apiLoadError}</span>
              <button
                type="button"
                className="btn-outline min-h-[36px] text-body-sm"
                onClick={() => setRefreshKey((k) => k + 1)}
              >
                {kitchenTerms.production.retry}
              </button>
            </div>
          )}

          {isLoadingPlan ? (
            <SkeletonList count={3} label={kitchenTerms.production.loadingPlan} />
          ) : planCatalog.length === 0 ? (
            <EmptyState
              icon="event_busy"
              title={kitchenTerms.production.empty.planTitle}
              description={kitchenTerms.production.empty.planDescription}
            />
          ) : (
          <>
          <div>
            <div className="flex items-center justify-between text-body-sm mb-1">
              <span className="text-on-surface-variant">{kitchenTerms.production.progressLabel}</span>
              <span className="font-semibold text-primary">{planPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-surface-variant overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${planPct}%` }}
              />
            </div>
          </div>

          <PlanProductionTable planLines={planLines} />

          <p className="text-body-sm text-on-surface-variant">
            {kitchenTerms.production.formulaHint}
          </p>
          <p className="text-body-sm text-on-surface-variant">
            {kitchenTerms.production.tableLegend}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleDownloadPlanPdf}
              disabled={isDownloadingPlan || planStatus === 'none'}
              className="btn-outline w-full sm:flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {isDownloadingPlan ? kitchenTerms.production.downloadingPdf : kitchenTerms.production.downloadPdf}
            </button>
            {hasActiveTodo ? (
              <button
                type="button"
                onClick={() => {
                  setView('sessions')
                  setSessionFilter('todo')
                }}
                className="btn-primary w-full sm:flex-1"
              >
                {kitchenTerms.production.continueInput}
              </button>
            ) : (
              <button type="button" onClick={startProductionToday} className="btn-primary w-full sm:flex-1">
                {kitchenTerms.production.startToday}
              </button>
            )}
          </div>
          </>
          )}
          <Link href="/kitchen/home" className="btn-outline block text-center">
            {kitchenTerms.production.backHome}
          </Link>
        </div>
      )}

      {view === 'sessions' && (
        <div className="space-y-3">
          {!selectedSession && (
            <>
              <div className="card space-y-3">
                <div>
                  <p className="text-headline-md text-primary">{kitchenTerms.production.sessionTitle}</p>
                  <p className="font-label-md font-mono text-on-surface-variant mt-1">Plan: {planCode}</p>
                  <p className="text-body-sm text-on-surface-variant mt-1">
                    {kitchenTerms.production.sessionListHint}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadPlanPdf}
                    disabled={isDownloadingPlan || planStatus === 'none'}
                    className="btn-outline inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-lg">download</span>
                    {isDownloadingPlan ? kitchenTerms.production.downloadingPdf : kitchenTerms.production.downloadPdf}
                  </button>
                  <button type="button" className="btn-primary" onClick={startProductionToday}>
                    {kitchenTerms.production.startToday}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="text-body-sm text-primary font-semibold underline-offset-2 hover:underline"
                >
                  {kitchenTerms.production.pickOtherDate}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'todo', label: kitchenTerms.production.filterTodo, count: todoSessions.length },
                    { key: 'history', label: kitchenTerms.production.filterHistory, count: historyCount },
                  ].map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setSessionFilter(filter.key as typeof sessionFilter)}
                      className={`rounded-lg px-3 py-2 text-body-sm font-semibold ${
                        sessionFilter === filter.key
                          ? 'bg-primary-container text-on-primary-container'
                          : 'bg-surface text-on-surface-variant border border-outline-variant'
                      }`}
                    >
                      {filter.label}
                      {filter.count > 0 && (
                        <span className="ml-1 opacity-80">({filter.count})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {visibleSessions.map((session) => {
                const posted = isPosted(session.status)
                const deletable = canDeleteSession(session)
                return (
                  <div key={session.ref} className="card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-label-md font-mono text-on-surface-variant">{session.ref}</p>
                        <p className="text-body-lg font-bold text-on-surface mt-1">{session.title}</p>
                        <p className="text-body-sm text-on-surface-variant mt-1">{getSessionSummary(session)}</p>
                        <p className="text-label-md text-on-surface-variant mt-2">{session.updatedAt}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {posted && <span className="badge-success">{kitchenTerms.production.badgePosted}</span>}
                        {deletable && (
                          <button
                            type="button"
                            onClick={() => setDeleteTargetRef(session.ref)}
                            className="text-status-danger p-1"
                            aria-label={kitchenTerms.production.deleteAria}
                          >
                            <span className="material-symbols-outlined text-xl">delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectSession(session.ref)}
                      className={`mt-3 w-full min-h-[44px] ${posted ? 'btn-outline' : 'btn-primary'}`}
                    >
                      {posted ? kitchenTerms.production.viewDetail : kitchenTerms.production.continueFill}
                    </button>
                  </div>
                )
              })}
              {visibleSessions.length === 0 && (
                <div className="card text-center space-y-3 text-body-sm text-on-surface-variant">
                  <p>
                    {sessionFilter === 'todo'
                      ? kitchenTerms.production.emptyTodo
                      : kitchenTerms.production.emptyHistory}
                  </p>
                  {sessionFilter === 'todo' && (
                    <button type="button" className="btn-primary w-full sm:w-auto" onClick={startProductionToday}>
                      {kitchenTerms.production.startToday}
                    </button>
                  )}
                </div>
              )}
              <Link href="/kitchen/home" className="btn-outline block text-center">
                {kitchenTerms.production.backHome}
              </Link>
            </>
          )}

          {selectedSession && editorStep === 'edit' && (
            <div className={!isPosted(selectedSession.status) ? 'relative pb-28' : undefined}>
              <div className="card space-y-3">
                <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
                  <div>
                    <p className="font-label-md font-mono text-on-surface-variant">{selectedSession.ref}</p>
                    <p className="text-headline-md text-primary mt-1">{kitchenTerms.production.editTitle}</p>
                    <p className="text-body-sm text-on-surface-variant">{selectedSession.title}</p>
                  </div>
                  <button type="button" className="btn-outline min-h-[44px]" onClick={closeSession}>
                    {kitchenTerms.production.back}
                  </button>
                </div>

                <FlowStepper
                  steps={[...PRODUCTION_FLOW_STEPS]}
                  activeStep={productionFlowActiveStep(selectedSession.status, editorStep)}
                />

              {isPosted(selectedSession.status) && filledLines.length > 0 && (
                <div className="rounded-lg bg-surface-container border border-outline-variant p-3 space-y-2">
                  <p className="text-body-sm font-semibold text-on-surface">{kitchenTerms.production.summaryVsPlan}</p>
                  {filledLines.map((line) => {
                    const planItem = planCatalog.find((item) => item.sku === line.productName)
                    if (!planItem) return null
                    const stock = stockQtyForProduct(stockByProduct, line.productName)
                    const dispatched = qtyForProduct(dispatchedByProduct, line.productName)
                    const need = productionNeed(planItem.plan, stock, dispatched)
                    const hint = getActualInputHint(line.qty, need)
                    return (
                      <div key={line.id} className="flex items-center justify-between gap-2 text-body-sm">
                        <span className="text-on-surface">{line.productName}</span>
                        <span className="text-on-surface-variant">
                          {line.qty} pcs · <span className={hint.className}>{hint.text}</span>
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="space-y-2">
                {sessionLineGroups.map((group) => (
                  <CollapsibleCategoryBlock
                    key={group.categoryName}
                    categoryName={group.categoryName}
                    itemCount={group.items.length}
                    defaultOpen={false}
                  >
                    <div className="space-y-2 p-2">
                      {group.items.map((line) => {
                        const planItem = planCatalog.find((item) => item.sku === line.productName)
                        const stock = stockQtyForProduct(stockByProduct, line.productName)
                        const dispatched = qtyForProduct(dispatchedByProduct, line.productName)
                        const planQty = line.planQty ?? planItem?.plan ?? null
                        const need = planQty !== null ? productionNeed(planQty, stock, dispatched) : 0
                        const inputHint = getActualInputHint(line.qty, need)
                        return (
                          <div key={line.id} className="rounded-lg border border-outline-variant p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-body-lg font-semibold text-on-surface flex-1">{line.productName}</p>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-center text-label-md">
                              <div>
                                <p className="text-on-surface-variant">{kitchenTerms.production.columns.stock}</p>
                                <p className="font-semibold text-on-surface">{stock}</p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">{kitchenTerms.production.columns.plan}</p>
                                <p className="font-semibold text-on-surface">{planQty === null ? '—' : planQty}</p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">{kitchenTerms.production.columns.need}</p>
                                <p
                                  className={`font-semibold ${planQty === null ? 'text-on-surface' : needCellClass(need)}`}
                                >
                                  {planQty === null ? '—' : formatNeedCell(need)}
                                </p>
                              </div>
                              <div>
                                <p className="text-on-surface-variant">{kitchenTerms.production.columns.actual}</p>
                                <p className="font-semibold text-on-surface">{line.qty}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <label className="text-body-sm text-on-surface-variant">{kitchenTerms.production.qtyLabel}</label>
                              <QtyStepper
                                value={line.qty}
                                min={0}
                                onChange={(qty) =>
                                  selectedSessionRef && updateLine(selectedSessionRef, line.id, { qty })
                                }
                                disabled={isPosted(selectedSession.status)}
                              />
                              <span className="text-body-sm text-on-surface-variant">{kitchenTerms.production.pcs}</span>
                            </div>
                            {!isPosted(selectedSession.status) && (
                              <p className={`text-label-md font-medium ${inputHint.className}`}>{inputHint.text}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </CollapsibleCategoryBlock>
                ))}
              </div>

              <div className="space-y-2">
                <label className="field-label" htmlFor="editor_note">
                  {kitchenTerms.production.noteLabel}
                </label>
                <textarea
                  id="editor_note"
                  rows={2}
                  value={editorNote}
                  onChange={(e) => setEditorNote(e.target.value)}
                  placeholder={kitchenTerms.production.notePlaceholder}
                  disabled={isPosted(selectedSession.status)}
                  className="input-field resize-none"
                />
              </div>

              {saveError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{saveError}</span>
                </div>
              )}

              {isPosted(selectedSession.status) && (
                <p className="text-body-sm text-on-surface-variant text-center">
                  {kitchenTerms.production.postedReadonly}
                </p>
              )}
              </div>

              {!isPosted(selectedSession.status) && (
                <StickyActionFooter>
                  <button type="button" className="btn-outline w-full min-h-[44px]" onClick={saveProductionResult}>
                    {kitchenTerms.production.saveDraft}
                  </button>
                  <button type="button" className="btn-primary w-full min-h-[44px]" onClick={openStockReview}>
                    {kitchenTerms.production.postStock}
                  </button>
                  <p className="text-label-md text-on-surface-variant text-center">
                    {kitchenTerms.production.footerSaveHint}
                  </p>
                </StickyActionFooter>
              )}
            </div>
          )}

          {selectedSession && editorStep === 'review' && (
            <div className="relative pb-28">
              <div className="card space-y-3">
                <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
                  <div>
                    <p className="font-label-md font-mono text-on-surface-variant">{selectedSession.ref}</p>
                    <p className="text-headline-md text-primary mt-1">{kitchenTerms.production.reviewTitle}</p>
                    <p className="text-body-sm text-on-surface-variant">{selectedSession.title}</p>
                  </div>
                  <button type="button" className="btn-outline min-h-[44px]" onClick={() => setEditorStep('edit')}>
                    {kitchenTerms.production.back}
                  </button>
                </div>

                <FlowStepper
                  steps={[...PRODUCTION_FLOW_STEPS]}
                  activeStep={productionFlowActiveStep(selectedSession.status, editorStep)}
                />

                <p className="text-body-sm text-on-surface-variant">
                  {kitchenTerms.production.reviewHint}
                </p>

                <ActionSummaryCard
                  items={filledLines.map((line) => ({
                    id: line.id,
                    title: line.productName,
                    value: `${line.qty} ${kitchenTerms.production.pcs}`,
                  }))}
                  totalLabel={kitchenTerms.production.totalLabel}
                  totalValue={`${filledLines.length} produk · ${filledTotalQty} ${kitchenTerms.production.pcs}`}
                />

                {editorNote.trim() && (
                  <div className="rounded-lg border border-outline-variant p-3">
                    <p className="text-label-md text-on-surface-variant">{kitchenTerms.production.note}</p>
                    <p className="text-body-sm text-on-surface mt-1">{editorNote}</p>
                  </div>
                )}
              </div>

              <StickyActionFooter>
                <button
                  type="button"
                  className="btn-primary w-full min-h-[44px]"
                  onClick={() => setIsPinModalOpen(true)}
                >
                  {kitchenTerms.production.confirmPost}
                </button>
              </StickyActionFooter>
            </div>
          )}
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={dismissToast} />}

      <PinKeypadModal
        open={isPinModalOpen}
        title={kitchenTerms.production.pin.title}
        description={kitchenTerms.production.pin.description}
        confirmLabel={kitchenTerms.production.pin.confirm}
        onClose={() => setIsPinModalOpen(false)}
        onConfirm={confirmPostToStock}
      />

      {isCreateOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-surface-card border border-outline-variant rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex items-center justify-between">
              <p className="text-headline-md text-primary">{kitchenTerms.production.create.title}</p>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="text-on-surface-variant"
                aria-label="Tutup"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.production.create.hint}
              </p>
              <div className="space-y-2">
                <label className="field-label" htmlFor="session_date">
                  {kitchenTerms.production.create.dateLabel}
                </label>
                <input
                  id="session_date"
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  className="input-field"
                />
                <p className="text-label-md text-on-surface-variant">
                  {kitchenTerms.production.create.sessionTitleLabel(
                    buildSessionTitle(sessionDate || kitchenTodayISO()),
                  )}
                </p>
              </div>
              {createError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{createError}</span>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 justify-end">
              <button type="button" className="btn-outline" onClick={() => setIsCreateOpen(false)}>
                {kitchenTerms.production.create.cancel}
              </button>
              <button type="button" className="btn-primary" onClick={() => createNewSession()}>
                {kitchenTerms.production.create.start}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTargetRef && (
        <div
          className="fixed inset-0 z-40 bg-inverse-surface/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-session-title"
        >
          <div className="w-full max-w-sm bg-surface-card border border-outline-variant rounded-xl shadow-sm p-4 space-y-4">
            <div>
              <p id="delete-session-title" className="text-headline-md text-primary">
                {kitchenTerms.production.deleteConfirm.title}
              </p>
              <p className="text-body-sm text-on-surface-variant mt-2">
                {kitchenTerms.production.deleteConfirm.message}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-outline flex-1 min-h-[44px]"
                onClick={() => setDeleteTargetRef(null)}
              >
                {kitchenTerms.production.deleteConfirm.cancel}
              </button>
              <button
                type="button"
                className="btn-danger flex-1"
                onClick={() => deleteDraftSession(deleteTargetRef)}
              >
                {kitchenTerms.production.deleteConfirm.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  )
}
